import React from 'react'
import {
  Box,
  Button,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import SearchIcon from '@mui/icons-material/Search'
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined'
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import KeyboardReturnOutlinedIcon from '@mui/icons-material/KeyboardReturnOutlined'
import AdminInternalNav from './AdminInternalNav'

const colors = {
  ink: '#06185f',
  muted: '#4c5d86',
  blue: '#0059f6',
  line: '#d9e3f4',
}

const controlSx = {
  height: 38,
  borderRadius: '8px',
  color: colors.ink,
  fontSize: 12,
  fontWeight: 800,
  backgroundColor: 'rgba(255,255,255,0.92)',
  boxShadow: '0 10px 22px rgba(19, 39, 84, 0.08)',
  '& fieldset': { borderColor: colors.line },
  '&:hover fieldset': { borderColor: 'rgba(0,89,246,0.35)' },
  '&.Mui-focused fieldset': { borderColor: colors.blue },
}

function OrderIcon({ dir = 'desc' }) {
  const ArrowIcon = dir === 'desc' ? ArrowDownwardOutlinedIcon : ArrowUpwardOutlinedIcon
  return (
    <Box sx={{ position: 'relative', width: 24, height: 24, lineHeight: 0, color: 'inherit' }}>
      <ArrowIcon fontSize="small" sx={{ position: 'absolute', left: 0, top: 0, color: 'inherit' }} />
      <AccessTimeOutlinedIcon sx={{ position: 'absolute', right: -2, bottom: -2, fontSize: 14, opacity: 0.9, color: 'inherit' }} />
    </Box>
  )
}

const estados = [
  { value: 'TODOS', label: 'Todos los estados' },
  { value: 'ACTIVO', label: 'Activo' },
  { value: 'FUTURO', label: 'Futuro' },
  { value: 'VENTA', label: 'Venta' },
  { value: 'BAJA', label: 'Baja' },
  { value: 'SIN_ASIGNAR', label: 'Sin asignar' },
  { value: 'SIN_CONSULTAS', label: 'Sin consultas' },
]

export default function ClientesHead({
  search,
  onSearchChange,
  estado,
  onEstadoChange,
  orderBy,
  onOrderByChange,
  orderDir,
  onOrderDirChange,
  showArchived,
  archivedTotal,
  onToggleArchived,
  onBackHome,
  onOpenNoAsignadas,
  onOpenCerradas,
  isFullScreen,
  onToggleFullScreen,
}) {
  const archivedButtonDisabled = !showArchived && Number(archivedTotal || 0) <= 0

  const PrimaryRow = (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', md: 'center' }}
      spacing={2}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{
            color: colors.ink,
            fontSize: 26,
            fontWeight: 900,
            lineHeight: 1.1,
          }}
        >
          Clientes
        </Typography>
        <Typography sx={{ mt: 0.5, color: colors.muted, fontSize: 12, fontWeight: 500 }}>
          Consultá la base general de clientes y abrí su historial desde el modal.
        </Typography>
      </Box>

      <Stack direction="row" alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} spacing={1} flexWrap="wrap" useFlexGap>
        <AdminInternalNav
          active="clientes"
          onHome={onBackHome}
          onNoAsignadas={onOpenNoAsignadas}
          onCerradas={onOpenCerradas}
          onClientes={() => {}}
        />
      </Stack>
    </Stack>
  )

  const ControlsRow = (
    <Stack
      direction={{ xs: 'column', lg: 'row' }}
      spacing={1}
      alignItems={{ xs: 'stretch', lg: 'center' }}
      justifyContent="space-between"
      sx={{ width: '100%' }}
    >
      <TextField
        size="small"
        value={search}
        onChange={(e) => onSearchChange?.(e.target.value)}
        placeholder="Buscar por nombre o teléfono..."
        sx={{
          minWidth: { xs: '100%', lg: 330 },
          '& .MuiOutlinedInput-root': controlSx,
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: colors.muted, fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
      />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
        <Tooltip title={archivedButtonDisabled ? 'No hay clientes archivados' : ''} arrow disableHoverListener={!archivedButtonDisabled}>
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <Button
              variant={showArchived ? 'contained' : 'outlined'}
              disabled={archivedButtonDisabled}
              onClick={() => onToggleArchived?.()}
              startIcon={showArchived ? <KeyboardReturnOutlinedIcon sx={{ fontSize: 18 }} /> : <ArchiveOutlinedIcon sx={{ fontSize: 18 }} />}
              sx={{
                height: 38,
                borderRadius: '8px',
                px: 1.4,
                color: showArchived ? '#fff' : colors.blue,
                borderColor: colors.line,
                backgroundColor: showArchived ? colors.blue : 'rgba(255,255,255,0.88)',
                boxShadow: '0 10px 22px rgba(19, 39, 84, 0.08)',
                fontSize: 12,
                fontWeight: 900,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: showArchived ? '#004be0' : 'rgba(0,89,246,0.35)',
                  backgroundColor: showArchived ? '#004be0' : 'rgba(0,89,246,0.08)',
                },
                '&.Mui-disabled': {
                  color: '#98a3b8',
                  borderColor: colors.line,
                  backgroundColor: 'rgba(255,255,255,0.7)',
                },
              }}
            >
              {showArchived ? 'Volver a clientes' : 'Ver archivados'}
            </Button>
          </Box>
        </Tooltip>

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <Select value={estado} onChange={(e) => onEstadoChange?.(e.target.value)} sx={controlSx}>
            {estados.map((item) => (
              <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={orderBy}
          onChange={(_, val) => { if (val) onOrderByChange?.(val) }}
          sx={{
            '& .MuiToggleButton-root': {
              height: 38,
              px: 1.4,
              color: colors.blue,
              borderColor: colors.line,
              backgroundColor: 'rgba(255,255,255,0.88)',
              fontSize: 12,
              fontWeight: 900,
              textTransform: 'none',
              '&.Mui-selected': {
                bgcolor: colors.blue,
                color: '#fff',
                borderColor: colors.blue,
                '&:hover': { bgcolor: '#004be0', borderColor: '#004be0' },
              },
            },
          }}
        >
          <ToggleButton value="fecha_carga" aria-label="ordenar-fecha-carga">Fecha carga</ToggleButton>
          <ToggleButton value="fecha_actividad" aria-label="ordenar-actividad">Última actividad</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={orderDir}
          onChange={(_, val) => { if (val) onOrderDirChange?.(val) }}
          sx={{
            '& .MuiToggleButton-root': {
              width: 38,
              height: 38,
              p: 0,
              color: colors.blue,
              borderColor: colors.line,
              backgroundColor: 'rgba(255,255,255,0.88)',
              '&.Mui-selected': {
                bgcolor: colors.blue,
                color: '#fff',
                borderColor: colors.blue,
                '&:hover': { bgcolor: '#004be0', borderColor: '#004be0' },
              },
            },
          }}
        >
          <Tooltip title="Más nuevos primero" arrow>
            <ToggleButton value="desc" aria-label="orden-desc">
              <OrderIcon dir="desc" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Más antiguos primero" arrow>
            <ToggleButton value="asc" aria-label="orden-asc">
              <OrderIcon dir="asc" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>

        <Tooltip title={isFullScreen ? 'Salir de vista amplia' : 'Vista amplia'}>
          <IconButton
            onClick={() => onToggleFullScreen?.(!isFullScreen)}
            sx={{
              width: 38,
              height: 38,
              borderRadius: '8px',
              border: `1px solid ${colors.line}`,
              color: colors.blue,
              backgroundColor: 'rgba(255,255,255,0.88)',
              '&:hover': { backgroundColor: 'rgba(0,89,246,0.08)' },
            }}
          >
            {isFullScreen ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  )

  const ToolbarContent = (
    <Stack spacing={2}>
      {PrimaryRow}
      {ControlsRow}
    </Stack>
  )

  if (isFullScreen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: '60px',
          left: 0,
          width: '100%',
          zIndex: 1050,
          height: '86px',
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
          '&:hover': { pointerEvents: 'auto' },
          '&:hover .MuiPaper-root': {
            transform: 'translateY(0)',
            opacity: 1,
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '95%',
            mt: 1,
            px: 2,
            py: 1,
            borderRadius: '10px',
            bgcolor: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            display: 'flex',
            alignItems: 'center',
            transform: 'translateY(-120%)',
            opacity: 0,
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s',
            border: `1px solid ${colors.line}`,
            boxShadow: '0 18px 38px rgba(19, 39, 84, 0.14)',
            pointerEvents: 'auto',
          }}
        >
          {ControlsRow}
        </Paper>

        <Fade in={true} timeout={1000}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '220px',
              height: '6px',
              bgcolor: colors.blue,
              borderBottomLeftRadius: 4,
              borderBottomRightRadius: 4,
              opacity: 0.8,
              pointerEvents: 'auto',
              cursor: 'pointer',
              zIndex: 1290,
              boxShadow: '0 2px 4px rgba(0,0,0,0.16)',
            }}
          />
        </Fade>
      </Box>
    )
  }

  return (
    <Box sx={{ mb: 2.2 }}>
      {ToolbarContent}
    </Box>
  )
}
